<template>
  <Transition name="modal-transition">
    <div v-if="modalActive" class="modal z-50 fixed inset-0 flex items-center justify-center">
      <div
        data-testid="modal-backdrop"
        class="absolute inset-0 z-40 bg-gray-900 opacity-50 modal-overlay"
        @click="close"
      ></div>

      <div
        class="modal-content z-50 w-full mx-2 sm:mx-6 md:mx-auto bg-white rounded-lg shadow-lg relative flex flex-col overflow-hidden"
        :class="modalWidthClass"
        :style="{ maxHeight: computedMaxHeight, minHeight: minHeight }"
      >
        <button
          v-if="showCloseButton"
          class="absolute top-2 right-2 md:top-4 md:right-4 z-10 p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary5"
          @click="close"
          aria-label="Fechar"
        >
          <svg
            class="w-6 h-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>

        <header
          v-if="$slots.header"
          class="pt-4 md:pt-6"
          :class="[
            hasHeaderBorder ? 'border-b-2 border-gray-100' : '',
            'w-full px-3 py-3 md:px-6 md:py-4'
          ]"
        >
          <slot name="header"></slot>
        </header>

        <main
          class="flex-1 flex flex-col overflow-y-auto"
          :class="noBodyPadding ? '' : 'px-3 py-3 md:px-6 md:py-4'"
        >
          <slot></slot>
        </main>

        <template v-if="$slots.footer">
          <footer
            :class="[
              hasFooterBorder ? 'border-t-2 border-gray-100' : '',
              'w-full px-3 py-3 md:px-6 md:py-4'
            ]"
          >
            <slot name="footer"></slot>
          </footer>
        </template>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useEscapeKey } from '@/utils/DOMEvents'

const props = defineProps({
  modalActive: { type: Boolean, default: false },
  hasFooterBorder: { type: Boolean, default: false },
  hasHeaderBorder: { type: Boolean, default: false },
  showCloseButton: { type: Boolean, default: true },
  isFilterModal: { type: Boolean, default: false },
  noBodyPadding: { type: Boolean, default: false },
  maxHeight: { type: String, default: '90vh' },
  minHeight: { type: String, default: 'auto' },
  maxWidth: { type: String, default: null }
})

const emit = defineEmits(['close'])

const close = () => {
  emit('close')
}

useEscapeKey(() => {
  if (props.modalActive) close()
})

const modalWidthClass = computed(() => {
  if (props.maxWidth) {
    return props.maxWidth
  }
  if (props.isFilterModal) {
    return 'max-w-md'
  }
  return 'max-w-4xl'
})

const computedMaxHeight = computed(() => {
  return props.maxHeight === '90vh' ? '90vh' : props.maxHeight
})
</script>

<style scoped>
.modal-transition-enter-active,
.modal-transition-leave-active {
  transition: opacity 0.3s ease;
}

.modal-transition-enter-from,
.modal-transition-leave-to {
  opacity: 0;
}
</style>
